// SSG-safe: JSON-LD now emitted via SEO/Helmet at render time.
import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { Button } from "@/components/ui/button";
import {
  Linkedin,
  Instagram,
  Shield,
  TrendingUp,
  Calculator,
  Gift,
  GraduationCap,
  Award,
  Building2,
  Briefcase,
  CheckCircle2,
} from "lucide-react";
import authorJann from "@/assets/author-jann.webp";

const PERSON_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Jann Kromminga",
  jobTitle: "Gründer & Finanzberater",
  worksFor: {
    "@type": "Organization",
    name: "FINABLE GmbH",
    url: "https://finable.de",
  },
  alumniOf: {
    "@type": "CollegeOrUniversity",
    name: "Universität Hamburg",
  },
  url: "https://finable.de/team/jann-kromminga",
  image: "https://finable.de/og-image.jpg",
  sameAs: [
    "https://www.linkedin.com/in/jann-kromminga/",
    "https://www.xing.com/profile/Jann_Kromminga2",
    "https://www.instagram.com/jann.kromminga/",
  ],
  knowsAbout: [
    "Altersvorsorge",
    "ETF",
    "Steueroptimierung",
    "Staatliche Förderungen",
    "Finanzberatung",
    "Versicherungen",
  ],
};

const focusAreas = [
  {
    icon: Shield,
    title: "Altersvorsorge & Rente",
    text: "Private, betriebliche und geförderte Vorsorgekonzepte für einen sorgenfreien Ruhestand.",
  },
  {
    icon: TrendingUp,
    title: "ETF & Kapitalanlage",
    text: "Kosteneffiziente, breit gestreute Portfolios statt teurer Provisionsprodukte.",
  },
  {
    icon: Calculator,
    title: "Steueroptimierung",
    text: "Legale Hebel nutzen – von Riester bis Rürup, von Vermögenswirksamen Leistungen bis BAV.",
  },
  {
    icon: Gift,
    title: "Staatliche Förderungen",
    text: "Zulagen, Arbeitgeberzuschüsse und Steuervorteile, die viele Arbeitnehmer verschenken.",
  },
];

const credentials = [
  { icon: GraduationCap, text: "Bachelor of Arts Sozialökonomie – Universität Hamburg (2020)" },
  { icon: Award, text: "Lizenzierter Versicherungsvermittler §34d GewO" },
  { icon: Award, text: "Lizenzierter Finanzanlagenvermittler §34f GewO" },
  { icon: Briefcase, text: "Gründer FINABLE GmbH (seit 2021)" },
  { icon: Building2, text: "Mitglied Industrie- und Handelskammer Hamburg" },
];

const stats = [
  { number: "250+", label: "Kunden beraten" },
  { number: "7+", label: "Jahre Erfahrung" },
  { number: "127+", label: "Bewertungen (4,7/5 auf Trustpilot)" },
];

export default function TeamJannKromminga() {
  useReveal();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Jann Kromminga – Gründer & Finanzberater | FINABLE"
        description="Jann Kromminga ist Gründer von FINABLE und lizenzierter Finanzberater (§34d/34f GewO). Sozialökonom, digital, unabhängig – Finanzberatung neu gedacht."
        canonical="https://finable.de/team/jann-kromminga"
        jsonLd={PERSON_SCHEMA}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 md:pt-40 pb-16 md:pb-24 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="absolute top-1/4 -right-20 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
          <div className="container relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <span className="reveal inline-block text-accent font-semibold uppercase tracking-wider text-sm">
                Gründer · Finanzberater
              </span>
              <h1 className="reveal mt-4 font-display text-4xl md:text-6xl font-extrabold text-balance leading-[1.05]">
                Jann Kromminga
                <span className="block text-accent mt-2">Gründer von FINABLE</span>
              </h1>
              <p className="reveal mt-6 text-lg md:text-xl text-white/85 leading-relaxed">
                Lizenzierter Versicherungs- und Finanzanlagenvermittler · §34d &amp; §34f GewO · Sozialökonom B.A.
              </p>
              <div className="reveal mt-8 flex flex-wrap gap-3 justify-center lg:justify-start">
                <a
                  href="https://www.linkedin.com/in/jann-kromminga/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-accent hover:text-accent-foreground transition-colors ring-1 ring-white/20"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://www.xing.com/profile/Jann_Kromminga2"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Xing"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-accent hover:text-accent-foreground transition-colors ring-1 ring-white/20 font-bold text-sm"
                >
                  X
                </a>
                <a
                  href="https://www.instagram.com/jann.kromminga/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-accent hover:text-accent-foreground transition-colors ring-1 ring-white/20"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="reveal relative">
              <div className="absolute -inset-4 bg-accent/20 rounded-3xl blur-2xl" aria-hidden />
              <div className="relative aspect-[4/5] max-w-md mx-auto rounded-3xl overflow-hidden shadow-elegant ring-1 ring-white/10">
                <img
                  src={authorJann}
                  alt="Jann Kromminga – Gründer von FINABLE"
                  className="w-full h-full object-cover object-[center_25%]"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* About */}
        <section className="py-20 md:py-24 bg-background">
          <div className="container max-w-3xl">
            <div className="reveal">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Über Jann</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-balance">
                Finanzberatung, <span className="text-accent">neu gedacht</span>
              </h2>
            </div>
            <div className="mt-8 space-y-5 text-lg text-foreground/85 leading-relaxed reveal">
              <p>
                Jann Kromminga hat Sozialökonomie an der Universität Hamburg studiert und 2020 seinen Bachelor of
                Arts abgeschlossen. Was er dort gelernt hat, hat ihn in einer Überzeugung bestärkt: Die
                Finanzbranche hat ein Problem. Zu viele schwarze Schafe, zu viel Komplexität, zu wenig Transparenz
                — und das ausgerechnet bei einem Thema das jeden betrifft.
              </p>
              <p>
                2021 hat er FINABLE gegründet. Nicht als klassische Finanzberatung, sondern als digitale
                Alternative für Menschen die kluge Entscheidungen treffen wollen ohne sich durch Papierkram,
                Verkaufsgespräche und undurchsichtige Provisionen kämpfen zu müssen. Der Anspruch:
                Finanzberatung so einfach und ehrlich wie sie sein sollte.
              </p>
              <p>
                Als lizenzierter Versicherungs- und Finanzanlagenvermittler nach §34d und §34f GewO berät Jann heute
                Arbeitnehmer und Selbstständige bundesweit — komplett digital, unabhängig und ohne
                Bankbindung.
              </p>
            </div>
          </div>
        </section>

        {/* Focus areas */}
        <section className="py-20 md:py-24 bg-muted/30">
          <div className="container">
            <div className="text-center max-w-2xl mx-auto reveal">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Schwerpunkte</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-balance">
                Worin Jann <span className="text-accent">Experte</span> ist
              </h2>
            </div>
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {focusAreas.map((f) => (
                <div
                  key={f.title}
                  className="reveal bg-card border border-border rounded-2xl p-6 hover:shadow-elegant hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/15 text-accent flex items-center justify-center">
                    <f.icon className="w-6 h-6" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold">{f.title}</h3>
                  <p className="mt-2 text-muted-foreground leading-relaxed">{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Credentials & Stats */}
        <section className="py-20 md:py-24 bg-background">
          <div className="container grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div className="reveal">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">
                Qualifikationen &amp; Zulassungen
              </span>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-balance">
                Geprüft, lizenziert, <span className="text-accent">unabhängig</span>.
              </h2>
              <ul className="mt-8 space-y-4">
                {credentials.map((c) => (
                  <li key={c.text} className="flex items-start gap-3">
                    <span className="mt-0.5 w-9 h-9 rounded-lg bg-accent/15 text-accent flex items-center justify-center shrink-0">
                      <c.icon className="w-5 h-5" />
                    </span>
                    <span className="text-foreground/85 leading-relaxed pt-1.5">{c.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="reveal grid sm:grid-cols-3 gap-4 lg:sticky lg:top-28">
              {stats.map((s) => (
                <div
                  key={s.label}
                  className="bg-gradient-hero text-white rounded-2xl p-6 text-center shadow-elegant"
                >
                  <div className="font-display text-4xl md:text-5xl font-extrabold text-accent">{s.number}</div>
                  <div className="mt-2 text-sm text-white/85 leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Personal */}
        <section className="py-20 md:py-24 bg-muted/30">
          <div className="container max-w-3xl reveal">
            <span className="text-primary font-semibold uppercase tracking-wider text-sm">Persönliches</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-balance">
              Sport &amp; <span className="text-accent">Disziplin</span>
            </h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">
              Neben der Arbeit ist Jann beim Rollstuhlbasketball aktiv, trainiert regelmäßig im Fitnessstudio
              und ist gerne mit dem Fahrrad unterwegs. Sport und Disziplin — zwei Eigenschaften die er auch in
              die Finanzberatung mitbringt.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Rollstuhlbasketball", "Fitness", "Rennrad"].map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border text-sm font-semibold text-foreground/80"
                >
                  <CheckCircle2 className="w-4 h-4 text-accent" /> {t}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative text-center max-w-2xl reveal">
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-balance">
              Lerne Jann <span className="text-accent">kennen</span>
            </h2>
            <p className="mt-6 text-lg text-white/85 leading-relaxed">
              Du willst wissen ob FINABLE zu Dir passt? Vereinbare ein kostenloses Erstgespräch direkt mit Jann
              — unverbindlich, digital und auf Augenhöhe.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Button asChild size="lg" variant="secondary" className="text-base px-8 py-6 h-auto">
                <a href="https://calendly.com/finable/kennenlerngespraech" target="_blank" rel="noopener noreferrer">
                  Kostenloses Erstgespräch vereinbaren
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="text-base px-8 py-6 h-auto bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
              >
                <Link to="/team">Zurück zum Team</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
